import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'
import { createRandom } from '@/lib/random'

interface BladeOptions {
    seed: number
    count: number
    width: number
    height: number
    xFrom: number
    xTo: number
    minHeight: number
    maxHeight: number
    minWidth: number
    maxWidth: number
    lean: number
}

const makeBlades = ({ seed, count, height, xFrom, xTo, minHeight, maxHeight, minWidth, maxWidth, lean }: BladeOptions): Array<string> => {
    const random = createRandom(seed)
    const blades: Array<string> = []

    for (let index = 0; index < count; index += 1) {
        const x = xFrom + random() * (xTo - xFrom)
        const bladeHeight = minHeight + Math.pow(random(), 0.85) * (maxHeight - minHeight)
        const bladeWidth = minWidth + random() * (maxWidth - minWidth)
        const tilt = (random() - 0.5) * lean
        const tipX = x + tilt
        const tipY = height - bladeHeight
        const controlY = height - bladeHeight * 0.55

        blades.push(
            `M${(x - bladeWidth / 2).toFixed(1)} ${height} `
            + `Q${(x - bladeWidth * 0.2 + tilt * 0.22).toFixed(1)} ${controlY.toFixed(1)} ${tipX.toFixed(1)} ${tipY.toFixed(1)} `
            + `Q${(x + bladeWidth * 0.3 + tilt * 0.3).toFixed(1)} ${controlY.toFixed(1)} ${(x + bladeWidth / 2).toFixed(1)} ${height}Z`
        )
    }

    return blades
}

const MEADOW = makeBlades({ seed: 7, count: 150, width: 1440, height: 320, xFrom: -20, xTo: 1460, minHeight: 40, maxHeight: 210, minWidth: 3, maxWidth: 8, lean: 90 })
const LEFT_CLUSTER = makeBlades({ seed: 21, count: 26, width: 520, height: 760, xFrom: -40, xTo: 380, minHeight: 240, maxHeight: 720, minWidth: 9, maxWidth: 22, lean: 260 })
const RIGHT_CLUSTER = makeBlades({ seed: 42, count: 24, width: 520, height: 760, xFrom: 140, xTo: 560, minHeight: 220, maxHeight: 700, minWidth: 9, maxWidth: 22, lean: 260 })

const MOTES = Array.from({ length: 14 }, (_, index) => {
    const random = createRandom(100 + index)

    return {
        left: `${8 + random() * 84}%`,
        top: `${18 + random() * 58}%`,
        size: 2 + random() * 3,
        delay: `${(random() * 6).toFixed(2)}s`,
        duration: `${(7 + random() * 6).toFixed(2)}s`,
        opacity: 0.35 + random() * 0.45
    }
})

/**
 * Procedural foreground grass for the hero: a low meadow across the bottom
 * and two tall, out-of-focus clusters framing the headline. Each layer sways
 * on its own rhythm and shifts with the cursor at a different depth.
 */
export const HeroGrass = () => {
    return (
        <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
            <div className='absolute inset-0'>
                {MOTES.map((mote, index) => (
                    <span
                        key={index}
                        className='motion-float absolute rounded-full bg-citron blur-[1px]'
                        style={{
                            left: mote.left,
                            top: mote.top,
                            width: mote.size,
                            height: mote.size,
                            opacity: mote.opacity,
                            animationDelay: mote.delay,
                            animationDuration: mote.duration,
                            boxShadow: '0 0 12px 2px rgba(226, 243, 122, 0.45)'
                        }}
                    />
                ))}
            </div>

            <div
                className='motion-parallax absolute inset-x-[-4%] bottom-0 h-[30%] will-change-transform'
                style={{ transform: 'translate3d(calc(var(--mx, 0) * -10px), calc(var(--my, 0) * -4px), 0)' } as CSSProperties}
            >
                <div className='motion-sway-soft h-full w-full [animation-duration:6.5s]'>
                    <svg viewBox='0 0 1440 320' preserveAspectRatio='none' className='h-full w-full blur-[1.2px]'>
                        <defs>
                            <linearGradient id='meadow-fill' x1='0' y1='1' x2='0' y2='0'>
                                <stop offset='0%' stopColor='#030402' />
                                <stop offset='55%' stopColor='#11170a' />
                                <stop offset='100%' stopColor='#9fae55' stopOpacity='0.55' />
                            </linearGradient>
                        </defs>

                        <g fill='url(#meadow-fill)'>
                            {MEADOW.map((d, index) => <path key={index} d={d} />)}
                        </g>
                    </svg>
                </div>
            </div>

            <div
                className='motion-parallax absolute bottom-0 left-[-6%] h-[78%] w-[min(46vw,540px)] will-change-transform max-sm:left-[-22%] max-sm:h-[58%] max-sm:w-[70vw]'
                style={{ transform: 'translate3d(calc(var(--mx, 0) * -34px), calc(var(--my, 0) * -14px), 0)' } as CSSProperties}
            >
                <div className='motion-sway h-full w-full [animation-duration:8.5s]'>
                    <svg viewBox='0 0 520 760' preserveAspectRatio='xMinYMax meet' className={cn('h-full w-full blur-[5px]')}>
                        <defs>
                            <linearGradient id='cluster-left-fill' x1='0' y1='1' x2='0' y2='0'>
                                <stop offset='0%' stopColor='#020301' />
                                <stop offset='60%' stopColor='#0d1207' />
                                <stop offset='100%' stopColor='#c8c27a' stopOpacity='0.6' />
                            </linearGradient>
                        </defs>

                        <g fill='url(#cluster-left-fill)'>
                            {LEFT_CLUSTER.map((d, index) => <path key={index} d={d} />)}
                        </g>
                    </svg>
                </div>
            </div>

            <div
                className='motion-parallax absolute bottom-0 right-[-6%] h-[70%] w-[min(42vw,500px)] will-change-transform max-sm:right-[-24%] max-sm:h-[50%] max-sm:w-[66vw]'
                style={{ transform: 'translate3d(calc(var(--mx, 0) * -28px), calc(var(--my, 0) * -12px), 0)' } as CSSProperties}
            >
                <div className='motion-sway h-full w-full [animation-delay:-3s] [animation-duration:9.5s]'>
                    <svg viewBox='0 0 520 760' preserveAspectRatio='xMaxYMax meet' className='h-full w-full blur-[4px]'>
                        <defs>
                            <linearGradient id='cluster-right-fill' x1='0' y1='1' x2='0' y2='0'>
                                <stop offset='0%' stopColor='#020301' />
                                <stop offset='62%' stopColor='#0e1408' />
                                <stop offset='100%' stopColor='#d9d38b' stopOpacity='0.55' />
                            </linearGradient>
                        </defs>

                        <g fill='url(#cluster-right-fill)'>
                            {RIGHT_CLUSTER.map((d, index) => <path key={index} d={d} />)}
                        </g>
                    </svg>
                </div>
            </div>
        </div>
    )
}
